import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { allSettled, dropTask } from 'ember-concurrency';
import { COMPANY_CREDIT } from 'sh-www/models/company-credit';
import {
  handleErrorResponseWithModal,
} from 'sh-www/utils/handles-errors';

const CLASS_NAME = 'SH-r-reports-company-credits';

const BASE_GENERAL_INVOICE_INCLUDES = [
  'related.cost-code',
];

class CompanyCreditsReportController extends Controller {
  @service('commands/cost-code-commands') costCodeCommandsService;
  @service errorTracker;
  @service('commands/sh-general-invoice-commands') shGeneralInvoiceCommandService;
  @service session;
  @service shData;
  @service store;

  costCodes = [];
  costCodesCurrentPage = 1;

  shGeneralPaidInvoices = [];
  shGeneralPaidInvoicesPagination = null;

  shGeneralUnpaidInvoices = [];
  shGeneralUnpaidInvoicesPagination = null;

  shGeneralDeletedInvoices = [];
  shGeneralDeletedInvoicesPagination = null;

  get baseClassName() {
    return CLASS_NAME;
  }

  @computed('session.currentUser.canViewReportsData','session.currentUser.company.canUseCredits')
  get canViewReport() {
    const { currentUser } = this.session;
    return currentUser.canViewReportsData
      && currentUser.company?.canUseCredits;
  }

  @computed('session.currentUser.isAdmin')
  get shGeneralInvoiceIncludes() {
    return this.session.currentUser.isAdmin
      ? [
          ...BASE_GENERAL_INVOICE_INCLUDES,
          'sh-general-invoice-transactions',
      ]
      : BASE_GENERAL_INVOICE_INCLUDES;
  }

  @action
  updateCompanyCostCodeCredits(costCodes, page) {
    this.setProperties({
      costCodes,
      costCodesCurrentPage: page,
    });
  }

  @action
  updateShGeneralPaidInvoices(shGeneralInvoices, pagination) {
    this.setProperties({
      shGeneralPaidInvoices: shGeneralInvoices,
      shGeneralPaidInvoicesPagination: pagination,
    });
  }

  @action
  updateShGeneralUnpaidInvoices(shGeneralInvoices, pagination) {
    this.setProperties({
      shGeneralUnpaidInvoices: shGeneralInvoices,
      shGeneralUnpaidInvoicesPagination: pagination,
    });
  }

  @action
  updateShGeneralDeletedInvoices(shGeneralInvoices, pagination) {
    this.setProperties({
      shGeneralDeletedInvoices: shGeneralInvoices,
      shGeneralDeletedInvoicesPagination: pagination,
    });
  }

  @action
  updateCompanyCreditFactoids(creditFactoids) {
    this.set('model.creditFactoids', creditFactoids);
  }

  @action
  async initializePage() {
    const initialPromises = [
      this.loadCompanyCostCodeCreditsTask.perform(),
      this.loadCreditPaidInvoicesTask.perform(),
      this.loadCreditUnpaidInvoicesTask.perform(),
    ];

    const [
      companyCostCodeCreditsResponse,
      creditPaidInvoicesResponse,
      creditUnpaidInvoicesResponse,
    ] = await allSettled(initialPromises);

    if (companyCostCodeCreditsResponse.state === 'fulfilled') {
      const companyCostCodeCredits = companyCostCodeCreditsResponse.value;
      this.setProperties(companyCostCodeCredits);
    }

    if (creditPaidInvoicesResponse.state === 'fulfilled') {
      const creditPaidInvoices = creditPaidInvoicesResponse.value;
      this.setProperties(creditPaidInvoices);
    }

    if (creditUnpaidInvoicesResponse.state === 'fulfilled') {
      const creditUnpaidInvoices = creditUnpaidInvoicesResponse.value;
      this.setProperties(creditUnpaidInvoices);
    }

    if (this.session.currentUser.isAdmin) {
      try {
        const creditDeletedInvoices = await this.loadCreditDeletedInvoicesTask.perform();
        this.updateShGeneralDeletedInvoices({ ...creditDeletedInvoices });
      } catch (e) {
        this.errorTracker.error(e, this, 'Problem loading deleted credit invoices');
      }
    }
  }

  @dropTask
  *loadCompanyCostCodeCreditsTask() {
    const { company } = this.session.currentUser;
    const costCodeIncludes = company.canUseCompanyTeams
        ? ['company-teams']
        : [];

    try {
      const costCodes = yield this.costCodeCommandsService.getCostCodesForCompany(
        company,
        costCodeIncludes,
      );

      return {
        costCodes,
        costCodesCurrentPage: 1,
      };
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(error, this, 'Problem loading cost code credits');
    }
  }

  @dropTask
  *loadCreditPaidInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: this.shGeneralInvoiceIncludes,
        filters: {
          company_id: currentUser.company.id,
          type: 'CompanyCredit',
          paid: true,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralPaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralPaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralPaidInvoices,
        shGeneralPaidInvoicesPagination,
      };
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(e, this, 'Problem loading paid credit invoices');
    }
  }

  @dropTask
  *loadCreditUnpaidInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: this.shGeneralInvoiceIncludes,
        filters: {
          company_id: currentUser.company.id,
          type: 'CompanyCredit',
          paid: false,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralUnpaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralUnpaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralUnpaidInvoices,
        shGeneralUnpaidInvoicesPagination,
      };
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(e, this, 'Problem loading unpaid credit invoices');
    }
  }

  @dropTask
  *loadCreditDeletedInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: BASE_GENERAL_INVOICE_INCLUDES,
        filters: {
          company_id: currentUser.company.id,
          type: 'CompanyCredit',
          only_trashed: currentUser.isAdmin,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralDeletedInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralDeletedInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralDeletedInvoices,
        shGeneralDeletedInvoicesPagination,
      }
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @dropTask
  *loadCompanyCreditFactoidsTask() {
    const { company } =  this.session.currentUser;
    try {
      return yield this.store.query('factoid', {
        type: COMPANY_CREDIT.FACTOID_TYPE,
        filter: {
          company_id: company.id,
        },
      });
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }
}

export default CompanyCreditsReportController;
